import { ChangeEventHandler, KeyboardEventHandler, SyntheticEvent, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { alpha, Button, Drawer, IconButton, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { FavoriteBorder } from '@mui/icons-material';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  getSeriesTitles,
  openRecent,
  openViewAll,
  resetSearch,
  TitleSidebarTabsValue,
  toggleDrawer,
  updateCurrentTab,
  updateSearchTerm,
  updateSelectedVersions,
  updateTitleSearchResults
} from '@/redux/slices/titleNavigationDrawerSlice';
import { applyFilter } from '@/redux/slices/dataTableSlice';
import { selectTitleNavigationDrawerIsSearching, selectTitleNavigationDrawerSlice } from '@/redux/selectors/titleNavigationDrawerSelectors';
import useDebounce from '@/components/_hooks/useDebounce';
import { Spinner } from '@/components/_common';
import PolarisIcon from '@/components/_common/PolarisIcon';
import TitlesList from '@/components/Focus/AvailsSchedule/TitleNavigationDrawer/TitlesList';
import SelectedTitleView from '@/components/Focus/AvailsSchedule/TitleNavigationDrawer/SelectedTitleView/SelectedTitleView';
import { FocusTheme } from '@/components/Focus/configs/theme';
import useScrollPosition from '@/hooks/useScrollPosition';

const TitleNavigationDrawer = () => {
  const dispatch = useAppDispatch();
  const { isOpen, searchTerm, selectedTitle, currentTab, selectedVersions } = useAppSelector(selectTitleNavigationDrawerSlice);
  const isSearching = useAppSelector(selectTitleNavigationDrawerIsSearching);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const searchFieldRef = useRef<HTMLInputElement | null>(null);
  const scrollY = useScrollPosition();
  const drawerHeight = scrollY > 0 ? '100dvh' : 'calc(100dvh - 128px)';

  const openDrawer = () => dispatch(toggleDrawer(true));
  const closeDrawer = () => dispatch(toggleDrawer(false));
  const viewAll = () => dispatch(openViewAll());
  const viewRecent = () => dispatch(openRecent());

  const updateSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
    dispatch(updateSearchTerm(e.target.value));
  };

  const clearSearch = () => {
    dispatch(resetSearch());
    searchFieldRef?.current?.focus();
  };

  const handleSearchKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      clearSearch();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const selectTab = (_: SyntheticEvent, newTab: TitleSidebarTabsValue) => {
    dispatch(updateCurrentTab(newTab));
  };

  const clearSelections = () => {
    dispatch(updateSelectedVersions());
    dispatch(applyFilter({ type: 'title_alt_client_id_filter', selections: undefined }));
  };

  const applyToTable = () => {
    dispatch(applyFilter({ type: 'title_alt_client_id_filter', selections: selectedVersions.map((version) => version.alpha_id).join(',') }));
  };

  useEffect(
    function searchTitlesOnDebounce() {
      if (!debouncedSearchTerm) {
        dispatch(updateTitleSearchResults([]));
        return;
      }

      dispatch(updateCurrentTab('all'));
      dispatch(getSeriesTitles());
    },
    [debouncedSearchTerm]
  );

  return (
    <StyledDrawer variant="permanent" open={isOpen} onClose={closeDrawer} height={drawerHeight}>
      <DrawerWrapper>
        {!isOpen && (
          <DrawerMenu>
            <Tooltip title="Expand" placement="right">
              <MenuItem onClick={openDrawer}>
                <PolarisIcon icon="panel_close_right" />
              </MenuItem>
            </Tooltip>
            <Tooltip title="View all" placement="right">
              <MenuItem onClick={viewAll}>
                <PolarisIcon icon="grid_outline" />
              </MenuItem>
            </Tooltip>
            {/* <Tooltip title="Favorites" placement="right">
              <MenuItem disabled>
                <FavoriteBorder />
              </MenuItem>
            </Tooltip> */}
            <Tooltip title="Recent" placement="right">
              <MenuItem onClick={viewRecent}>
                <PolarisIcon icon="history" />
              </MenuItem>
            </Tooltip>
          </DrawerMenu>
        )}
        {isOpen && (
          <>
            <DrawerHeader>
              <DrawerHeaderTitle>Find a Title</DrawerHeaderTitle>
              <CollapseButton onClick={closeDrawer}>
                <PolarisIcon icon="panel_close_left" />
              </CollapseButton>
            </DrawerHeader>

            {!selectedTitle && (
              <>
                <SearchField
                  value={searchTerm}
                  variant="filled"
                  placeholder="Search titles ..."
                  autoComplete="off"
                  inputRef={searchFieldRef}
                  onChange={updateSearch}
                  onKeyDown={handleSearchKeyDown}
                  InputProps={{
                    startAdornment: <PolarisIcon icon="search" height={24} />,
                    endAdornment:
                      searchTerm.length > 0 ? (
                        isSearching ? (
                          <SearchSpinner size={24} />
                        ) : (
                          <IconButton onClick={clearSearch}>
                            <PolarisIcon icon="close" />
                          </IconButton>
                        )
                      ) : null
                  }}
                  fullWidth
                />
                <StyledTabs value={currentTab} onChange={selectTab}>
                  <StyledTab value="all" label="View all" />
                  {/* <StyledTab disabled value="favorites" label="Favorites" /> */}
                  <StyledTab value="recent" label="Recent" />
                </StyledTabs>
                <TitlesList />
              </>
            )}

            {selectedTitle && <SelectedTitleView />}

            <ActionButtonsContainer>
              <ClearButton variant="outlined" onClick={clearSelections} disabled={isSearching || selectedVersions.length === 0}>
                Clear Selections
              </ClearButton>
              <ApplyButton variant="contained" color="secondary" onClick={applyToTable} disabled={isSearching || selectedVersions.length === 0}>
                Apply to Schedule <PolarisIcon icon="right" height={18} />
              </ApplyButton>
            </ActionButtonsContainer>
          </>
        )}
      </DrawerWrapper>
    </StyledDrawer>
  );
};

export default TitleNavigationDrawer;

const StyledDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'open' })<{ height: string }>(({ theme, open, height }) => ({
  '.MuiDrawer-paper': {
    height,
    position: 'sticky',
    alignSelf: 'start',
    width: open ? 500 : 72,
    padding: theme.spacing(open ? 2 : 1),
    backgroundColor: theme.palette.background.paper,
    transition: `width 0.3s ${theme.transitions.easing.sharp}, height 0.2s ${theme.transitions.easing.sharp}`,
    top: 0,
    marginTop: -25,
    borderTop: `2px solid ${theme.palette.divider}`
  }
}));

const DrawerWrapper = styled.div(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  rowGap: theme.spacing(2)
}));

const DrawerMenu = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  rowGap: theme.spacing(2)
}));

const MenuItem = styled(IconButton)(({ theme, disabled }) => ({
  svg: {
    color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
    width: 24,
    height: 24
  }
}));

const DrawerHeader = styled.div(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(0, 1)
}));

const DrawerHeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(19),
  fontWeight: 500,
  fontStyle: 'medium'
}));

const CollapseButton = styled(IconButton)(({ theme, disabled }) => ({
  svg: {
    color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
    width: 24,
    height: 24
  }
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  borderRadius: 6,
  padding: theme.spacing(0.5),
  overflow: 'visible',
  minHeight: 'auto',
  '& .MuiTabs-flexContainer': {
    gap: 1
  },
  '& .MuiTabs-indicator': {
    display: 'none'
  }
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  flex: 1,
  borderRadius: 6,
  transition: 'all 0.2s ease-in-out',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.background.paper
  }
}));

const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root.MuiFilledInput-root': {
    transition: 'background-color 0.2s ease-in-out',
    backgroundColor: theme.palette.background.default,
    '&:hover, &:focus, &:focus-within': {
      backgroundColor: theme.palette.background.paper
    },
    '& svg': {
      color: theme.palette.grey[500]
    }
  },
  '& input': {
    fontSize: theme.typography.pxToRem(16),
    padding: theme.spacing(2)
  }
}));

const SearchSpinner = styled(Spinner)(({ theme }) => ({
  right: theme.spacing(2),
  left: 'auto '
}));

const ActionButtonsContainer = styled.div(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1)
}));

const ClearButton = styled(Button)(({ theme }) => ({
  flex: '1 1 40%',
  display: 'flex',
  alignItems: 'center',
  transition: 'all 0.2s ease-in-out',
  color: (theme as FocusTheme).palette.neutral['40'],
  borderColor: (theme as FocusTheme).palette.neutral['40'],
  padding: theme.spacing(0.75, 2),
  '&:hover': {
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main
  }
}));

const ApplyButton = styled(Button)(({ theme }) => ({
  flex: '1 1 60%',
  display: 'flex',
  alignItems: 'center',
  columnGap: theme.spacing(0.5),
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.8)
  }
}));
